/**
 * Decision state for the post-sign-in passkey offer (CLA-893).
 *
 * Two scopes, deliberately different:
 *
 * - **Per user** (`claro:passkey-offer:user:<id>`) — how many times we've asked
 *   this person on this browser. Keyed by user id so someone signing in fresh
 *   on a borrowed machine still gets the offer rather than inheriting the
 *   previous occupant's "not now" (the same reasoning as the iOS flag).
 *
 * - **Per machine** (`claro:passkey-offer:shared`) — "this is a shared
 *   computer", which is a property of the MACHINE, not of whoever happened to
 *   answer. It suppresses the offer for everyone who ever uses this browser,
 *   because the hazard it describes belongs to the browser profile: a passkey
 *   in a shared profile is offered to the next person in the credential
 *   picker, and would sign them in as its owner. Storing that per-user would
 *   let the next clinician be prompted to create exactly the credential the
 *   first one just warned us about.
 *
 * Nothing here is PHI — a user id and a counter. See the note in
 * `clearSensitiveStorage` about why these keys must survive logout.
 */

const PREFIX = 'claro:passkey-offer'
const SHARED_KEY = `${PREFIX}:shared`
const ONBOARDING_KEY = `${PREFIX}:onboarding-complete`

/** Ask at most twice, ever. Beyond that, Settings → Security is the path. */
export const MAX_ASKS = 2

function userKey(userId: number): string {
  return `${PREFIX}:user:${userId}`
}

function readInt(key: string): number {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return 0
    const n = Number.parseInt(raw, 10)
    return Number.isFinite(n) && n > 0 ? n : 0
  } catch {
    return 0
  }
}

/** True when this browser has been marked a shared computer by anyone. */
export function isSharedComputer(): boolean {
  try {
    return localStorage.getItem(SHARED_KEY) === '1'
  } catch {
    return false
  }
}

/** Permanently suppress the offer for every user of this browser. */
export function markSharedComputer(): void {
  try {
    localStorage.setItem(SHARED_KEY, '1')
  } catch {
    /* storage unavailable — the offer simply reappears next sign-in */
  }
}

export function asksSoFar(userId: number): number {
  return readInt(userKey(userId))
}

export function recordAsk(userId: number): void {
  try {
    localStorage.setItem(userKey(userId), String(asksSoFar(userId) + 1))
  } catch {
    /* ignore */
  }
}

/**
 * Stop asking this user on this browser — used both when they enrol (nothing
 * left to offer) and when the browser reports they already have a credential
 * for this device.
 */
export function silenceForUser(userId: number): void {
  try {
    localStorage.setItem(userKey(userId), String(MAX_ASKS))
  } catch {
    /* ignore */
  }
}

/**
 * Mark that the onboarding wizard just finished (CLA-1013).
 *
 * The sign-in cookie below cannot cover this, and deliberately so — its whole
 * meaning is "THIS page load followed a sign-in", which is why it lives for
 * only 300 seconds. The backend says as much:
 *
 *   > the wizard has its own passkey step ... this cookie expires long before
 *   > a wizard run finishes anyway
 *
 * So for a brand-new owner the wizard's `PasskeyStep` was the ONE offer they
 * would ever see, and skipping it was final until their next sign-in. For a
 * self-serve signup that next sign-in is an email round-trip — the exact
 * friction a passkey removes. Grace Chau skipped it, never got back in, and
 * described the result as "I feel like an old person, I'm just struggling
 * getting in" (CLA-1011).
 *
 * A durable marker rather than a longer cookie: stretching the cookie would
 * blur what it means, and the two triggers answer different questions.
 */
export function markOnboardingComplete(): void {
  try {
    localStorage.setItem(ONBOARDING_KEY, '1')
  } catch {
    /* storage unavailable — the offer simply waits for the next sign-in */
  }
}

/** Read and CLEAR the onboarding marker. One-shot, like the cookie. */
function consumeOnboardingMarker(): boolean {
  try {
    const present = localStorage.getItem(ONBOARDING_KEY) === '1'
    if (present) localStorage.removeItem(ONBOARDING_KEY)
    return present
  } catch {
    return false
  }
}

/**
 * Read and CLEAR the one-shot server marker.
 *
 * The backend sets `claro_passkey_offer` on any web sign-in that did not use a
 * passkey. Clearing on read is what makes it one-shot: a reload, a navigation,
 * or a second tab must not re-trigger the offer.
 */
function consumeOfferCookie(): boolean {
  try {
    const present = document.cookie
      .split(';')
      .some((part) => part.trim().startsWith('claro_passkey_offer='))

    if (present) {
      document.cookie = 'claro_passkey_offer=; Max-Age=0; Path=/'
    }
    return present
  } catch {
    return false
  }
}

let signalCache: boolean | null = null

/**
 * Did THIS page load follow a passwordless sign-in?
 *
 * Memoised rather than read per-render, because reading the cookie also
 * destroys it. Without the cache, React's StrictMode double-render (and any
 * later re-render) would find the cookie already gone and silently decide not
 * to offer — the prompt would work in production and vanish in development,
 * which is the worst possible failure mode for something you only see once.
 */
export function signalledThisPageLoad(): boolean {
  if (signalCache === null) {
    // Both are consumed on the first read, and BOTH must be read even when the
    // first one wins — leaving an unconsumed marker behind would re-offer on
    // the next navigation. `||` short-circuits, so they are evaluated into
    // locals first.
    const fromCookie = consumeOfferCookie()
    const fromOnboarding = consumeOnboardingMarker()
    signalCache = fromCookie || fromOnboarding
  }
  return signalCache
}

/** Test seam — resets the per-page-load memo. */
export function resetSignalCacheForTests(): void {
  signalCache = null
}
